/**
 * Where to land after signing in.
 *
 * This exists because "return to where you were going" was, until now, honoured
 * by exactly one code path: the Google button. Everything else — the password
 * form, the e-mail-verification step, the route guard — navigated to '/' with
 * the destination hard-coded. That was invisible while the only caller was the
 * MCP authorize page, which renders the login form in place and never
 * navigates.
 *
 * The Garmin callback made it matter: the consent redirect arrives at
 * /garmin?oauth_token=…&oauth_verifier=…, and the verifier is single-use and
 * short-lived. Dropping the query string on the way through login does not send
 * someone to the wrong page — it destroys the consent, leaves a pending row
 * unconsumed until it expires, and sends them back to Garmin to start again.
 *
 * The rule: one representation of the destination (a same-site path, query
 * string included), carried on the /login URL, honoured by every branch that
 * can end a sign-in.
 */

const NEXT_PARAM = 'next'

/**
 * Accepts only a path on this site.
 *
 * The value reaches us from a query parameter, so it is attacker-controlled:
 * anything carrying a scheme or a host would turn our own login into an open
 * redirect, which is a credible phishing primitive precisely because the link
 * starts on our domain. Mirrors safeReturnPath in
 * services/auth/adapters/httpapi/oauth.go — same reasoning, same rejections.
 */
export function safeNext(raw: string | null | undefined): string | null {
  // Must be a path. A value carrying a scheme cannot start with '/', so this
  // one check already excludes 'https://…' and 'javascript:…'.
  if (!raw?.startsWith('/')) return null
  // '//host' is protocol-relative and '/\host' is treated as such by some
  // browsers: both are absolute URLs wearing a path's clothes, and both start
  // with '/', so they need excluding by hand.
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null
  // Deliberately no `new URL(raw, window.location.origin)` cross-check: it adds
  // nothing the two rules above have not already decided, and it makes a pure
  // string predicate depend on a global that does not exist everywhere this
  // runs — where it threw, the catch turned every safe path into "unsafe", and
  // the destination was dropped exactly as before this module existed.
  return raw
}

/** The current location as a destination string, query string included. */
export function currentPath(location: { pathname: string; search: string }): string {
  return `${location.pathname}${location.search}`
}

/** The /login URL that will come back to `destination` afterwards. */
export function loginUrlFor(destination: string): string {
  const safe = safeNext(destination)
  return safe ? `/login?${NEXT_PARAM}=${encodeURIComponent(safe)}` : '/login'
}

/** Reads the destination off a /login URL, or null when there is none. */
export function nextFrom(params: URLSearchParams): string | null {
  return safeNext(params.get(NEXT_PARAM))
}
